import { useEffect } from 'react';

import { useLocalStorage } from '@mantine/hooks';
import { unique } from 'radash';
import { InboxSort, NotificationPriorityHelpers } from 'src/helpers/NotificationPriorityHelpers';
import useSWRImmutable from 'swr/immutable';
import useSWRInfinite from 'swr/infinite';

import { InboxNotification } from 'models';
import { NotificationHelpers } from 'helpers/NotificationHelpers';

import { useCurrentUser } from 'hooks/use-current-user';
import { useSnapshot } from 'hooks/use-safe-snapshot';
import { NotificationService } from 'services/NotificationService';
import { state as inboxState } from 'state/inbox';

// "cached" notifications for local filtering
const loadedNotifications = new Map<string, InboxNotification>();

const useInbox = () => {
    const { opened, filters } = useSnapshot(inboxState);
    const { categoryCounts, isLoading } = useNotificationCounts(null);

    return {
        ...filters,
        opened,
        isFiltered:
            filters.category !== null || filters.unreadOnly || Boolean(filters.team) || Boolean(filters.company),
        isLoading,
        unreadCount: categoryCounts.all.unread,
    };
};

const useInboxFilterCounts = () => {
    const { team, company } = useSnapshot(inboxState).filters;

    return useNotificationCounts(team, company);
};

const useNotificationCounts = (team: string | null, company: string | null = null) => {
    const user = useCurrentUser();

    const { data, isLoading } = useSWRImmutable(user ? NotificationService.countsKey(team, company) : null, () =>
        NotificationService.counts(team, company),
    );

    return {
        isLoading,
        categoryCounts: NotificationHelpers.getCategoryCounts(data?.byType ?? {}),
    };
};

const useInboxSort = () =>
    useLocalStorage<InboxSort>({ key: 'inboxSort', defaultValue: InboxSort.PRIORITY, getInitialValueInEffect: false });

const useInboxQuery = () => {
    const { category, unreadOnly, team, company } = useSnapshot(inboxState).filters;
    const [sort] = useInboxSort();

    return { ...NotificationHelpers.getQuery(category), team, company, unread: unreadOnly, sort };
};

const useInboxNotifications = () => {
    const user = useCurrentUser();
    const query = useInboxQuery();

    const { data, size, setSize, isLoading, isValidating, mutate } = useSWRInfinite(
        (_pageIndex, previousPage: InboxNotification[] | null) => {
            if (!user || (previousPage && previousPage.length < NotificationService.pageSize)) {
                return null;
            }

            // Pages by rank and date rather than page number: new or just-read notifications would shift numbered pages,
            // duplicating or skipping items in the appended list.
            const lastNotification = previousPage?.at(-1);
            const before = lastNotification && { rank: lastNotification.rank, createdAt: lastNotification.createdAt };

            return ['/notifications/inbox', query, before] as const;
        },
        ([, pageQuery, before]) => NotificationService.inbox({ ...pageQuery, before }),
        { revalidateFirstPage: false },
    );

    // useSWRInfinite uses internal cache keys, so we need to "register" the mutate function for reuse
    useEffect(() => NotificationService.registerInboxList(mutate), [mutate]);

    useEffect(() => {
        for (const notification of data?.flat() ?? []) {
            loadedNotifications.set(notification.id, notification);
        }
    }, [data]);

    const pages = data ?? [];
    const lastPage = pages.at(-1);
    const preview = isLoading ? getPreview(query) : null;

    return {
        notifications: preview ?? unique(pages.flat(), (notification) => notification.id),
        isLoading: isLoading && !preview,
        isPreview: Boolean(preview),
        isLoadingMore: isValidating && size > pages.length,
        hasMore: Boolean(lastPage && lastPage.length === NotificationService.pageSize),
        loadMore: () => setSize(size + 1),
    };
};

const getPreview = (query: ReturnType<typeof useInboxQuery>): InboxNotification[] | null => {
    if (!loadedNotifications.size) {
        return null;
    }

    return [...loadedNotifications.values()]
        .filter((notification) => NotificationHelpers.matchesQuery(notification, query))
        .sort(
            (first, second) =>
                NotificationPriorityHelpers.getRank(first, query.sort) -
                    NotificationPriorityHelpers.getRank(second, query.sort) ||
                new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime(),
        )
        .slice(0, NotificationService.pageSize);
};

export { useInbox, useInboxFilterCounts, useInboxNotifications, useInboxQuery, useInboxSort, useNotificationCounts };
